/**
 * API Configuration
 * Centralized API client configuration with interceptors and error handling
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiResponse, ErrorResponse } from '../types';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';

// API Configuration
export const API_CONFIG = {
  baseURL: import.meta.env.VITE_API_BASE_URL || 'https://api.prindox.com',
  timeout: 120000, // 2 minutes for regular requests
  retryAttempts: 3,
  retryDelay: 1000,
} as const;

// Token management - Enhanced for Capacitor Native Persistence
class TokenManager {
  private static readonly TOKEN_KEY = 'scan2print_token';
  private static readonly REFRESH_TOKEN_KEY = 'scan2print_refresh_token';
  
  // In-memory cache for synchronous access in interceptors
  private static _token: string | null = null;
  private static _refreshToken: string | null = null;

  /**
   * Initialize tokens from storage (Call this on app startup)
   */
  static async init(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      const { value: token } = await Preferences.get({ key: this.TOKEN_KEY });
      const { value: refreshToken } = await Preferences.get({ key: this.REFRESH_TOKEN_KEY });
      this._token = token;
      this._refreshToken = refreshToken;
    } else {
      this._token = localStorage.getItem(this.TOKEN_KEY);
      this._refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
    }
    console.log('[TokenManager] Initialized. Authenticated:', !!this._token);
  }

  static getToken(): string | null {
    return this._token;
  }

  static async setToken(token: string): Promise<void> {
    this._token = token;
    if (Capacitor.isNativePlatform()) {
      await Preferences.set({ key: this.TOKEN_KEY, value: token });
    } else {
      localStorage.setItem(this.TOKEN_KEY, token);
    }
  }

  static getRefreshToken(): string | null {
    return this._refreshToken;
  }

  static async setRefreshToken(token: string): Promise<void> {
    this._refreshToken = token;
    if (Capacitor.isNativePlatform()) {
      await Preferences.set({ key: this.REFRESH_TOKEN_KEY, value: token });
    } else {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
    }
  }

  static async clearTokens(): Promise<void> {
    this._token = null;
    this._refreshToken = null;
    if (Capacitor.isNativePlatform()) {
      await Preferences.remove({ key: this.TOKEN_KEY });
      await Preferences.remove({ key: this.REFRESH_TOKEN_KEY });
    } else {
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    }
  }

  static isAuthenticated(): boolean {
    return !!this._token;
  }
}

// Create axios instance
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_CONFIG.baseURL,
    timeout: API_CONFIG.timeout,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - Add auth token
  client.interceptors.request.use(
    (config: AxiosRequestConfig) => {
      const token = TokenManager.getToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add request timestamp for debugging
      if (import.meta.env.DEV) {
        console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - Handle errors and token refresh
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Handle 401 - Unauthorized
      if (error.response?.status === 401 && !originalRequest._retry) {
        
        // Don't refresh if it's already an auth-related request
        if (originalRequest.url?.includes('/auth/login') || 
            originalRequest.url?.includes('/auth/refresh-token') ||
            originalRequest.url?.includes('/auth/otp')) {
            return Promise.reject(error);
        }

        originalRequest._retry = true;

        try {
          // Try to refresh token
          const refreshToken = TokenManager.getRefreshToken();
          if (refreshToken) {
            const response = await axios.post(`${API_CONFIG.baseURL}/api/v1/auth/refresh-token`, {
              refresh_token: refreshToken,
            });

            const { access_token, refresh_token: new_refresh_token } = response.data;
            await TokenManager.setToken(access_token);
            
            if (new_refresh_token) {
              await TokenManager.setRefreshToken(new_refresh_token);
            }

            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${access_token}`;
            }
            return client(originalRequest);
          }
        } catch (refreshError) {
          console.error('[Token Refresh Error] Clearing tokens.');
        }

        // If refresh fails, clear tokens and redirect
        await TokenManager.clearTokens();
        if (!Capacitor.isNativePlatform()) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      return Promise.reject(error);
    }
  );

  return client;
};

// Create the main API client
export const apiClient = createApiClient();

// Export token manager for use in auth context
export { TokenManager };

// Generic API response wrapper
export const handleApiResponse = <T>(response: AxiosResponse<T>): T => {
  return response.data;
};

// Generic error handler — throws an Error so err.message is always readable in catch blocks
export const handleApiError = (error: any): never => {
  if (error.response?.data) {
    const data = error.response.data;
    let msg: string;

    if (typeof data.message === 'string') {
      msg = data.message;
    } else if (typeof data.detail === 'string') {
      msg = data.detail;
    } else if (Array.isArray(data.detail)) {
      // FastAPI 422 validation errors: [{loc: [...], msg: "..."}]
      msg = data.detail.map((d: any) => d.msg || JSON.stringify(d)).join(', ');
    } else if (typeof data.error === 'string') {
      msg = data.error;
    } else if (typeof data === 'string') {
      msg = data;
    } else {
      msg = 'An error occurred. Please try again.';
    }

    const err = new Error(msg) as any;
    err.message = msg;
    err.detail = data.detail;
    err.status_code = data.status_code || error.response.status;
    err.responseData = data;
    throw err;
  }

  // Network / CORS error — no response body available
  const networkErr = new Error(error.message || 'Network error. Please check your connection.') as any;
  networkErr.status_code = 0;
  throw networkErr;
};

// Utility function to create typed API responses
export const createApiResponse = <T>(data: T, success = true): ApiResponse<T> => ({
  success,
  data,
});

// File upload configuration
export const createFormDataConfig = (): AxiosRequestConfig => ({
  headers: {
    'Content-Type': 'multipart/form-data',
  },
  timeout: 5 * 60 * 1000, // 5 minutes for file uploads
});

// Download configuration
export const createDownloadConfig = (): AxiosRequestConfig => ({
  responseType: 'blob',
  timeout: 2 * 60 * 1000, // 2 minutes for downloads
});
